import { Button } from '@/components/ui/button';
import {
  WhatsappLogo, Envelope, ChartBar, Path, Lightbulb, Strategy, Compass,
} from '@phosphor-icons/react';
import { CONTACT } from '@/lib/constants';

const GENERIC_CONSULTATION_WHATSAPP = `https://wa.me/${CONTACT.WHATSAPP_RAW}?text=${encodeURIComponent(
`Hello Aurea Digital Solutions.

I would like to discuss advisory or transformation support following the AWSAI assessment.

Organisation:
Contact Person:
Email:
Phone:
Topic of Interest:

Please advise on the next steps.`
)}`;

const GENERIC_CONSULTATION_EMAIL = `mailto:${CONTACT.EMAIL}?subject=${encodeURIComponent(
  'AWSAI Advisory & Transformation Enquiry'
)}&body=${encodeURIComponent(
`Dear Aurea Digital Solutions,

I would like to discuss advisory or transformation support following the AWSAI assessment.

Organisation:
Contact Person:
Email:
Phone:
Topic of Interest:

Please advise on the next steps.

Thank you.`
)}`;

// ---------- From Diagnostic Insight to Practical Improvement ----------

export const InsightToImprovementSection = () => (
  <section id="path-to-improvement" className="py-20 lg:py-28 bg-white" data-testid="insight-improvement-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-10">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">
          The Journey
        </div>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-4">
          From Diagnostic Insight to Practical Improvement
        </h2>
        <p className="text-slate-600 leading-relaxed text-base">
          The AWSAI assessment helps organisations move from general concern to structured action.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            icon: ChartBar,
            step: '01',
            title: 'Free Assessment',
            text: 'Provides an initial view of workflow stability and AI readiness, with an online dashboard result.',
          },
          {
            icon: Lightbulb,
            step: '02',
            title: 'Professional Diagnostic Report',
            text: 'Provides deeper interpretation, benchmarking, and management recommendations in a formal report.',
          },
          {
            icon: Path,
            step: '03',
            title: 'Advisory & Transformation',
            text: 'Aurea Digital Solutions can assist through consultation, workflow mapping, AI readiness review, workshops, and SMART-Workflow transformation planning.',
          },
        ].map(card => (
          <div key={card.step} className="border border-slate-200 rounded-xl p-7 bg-gradient-to-b from-white to-slate-50">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                <card.icon size={20} weight="duotone" />
              </div>
              <div className="text-[11px] tracking-[0.22em] font-bold text-teal-700">STEP {card.step}</div>
            </div>
            <div className="text-lg font-bold text-slate-900 mb-2">{card.title}</div>
            <p className="text-sm text-slate-600 leading-relaxed">{card.text}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 p-5 bg-slate-50 border-l-4 border-teal-600 rounded-r-lg max-w-3xl">
        <p className="text-slate-700 leading-relaxed text-[15px]">
          Executive Review and SMART-Workflow Transformation engagements are available through
          consultation and a customised proposal that reflects the organisation&rsquo;s context and priorities.
        </p>
      </div>
    </div>
  </section>
);

// ---------- Advisory & Transformation Support ----------

const ADVISORY = [
  {
    icon: Strategy,
    title: 'Executive Review',
    desc: 'A structured review session to interpret diagnostic findings, validate key risks, and identify priority management actions.',
  },
  {
    icon: Compass,
    title: 'Workflow Mapping and Process Review',
    desc: 'Detailed examination of critical workflows to identify bottlenecks, duplication, control weaknesses, and improvement opportunities.',
  },
  {
    icon: Lightbulb,
    title: 'AI Readiness Assessment',
    desc: 'A focused review of governance, data readiness, staff capability, and technology conditions required for responsible AI adoption.',
  },
  {
    icon: Path,
    title: 'SMART-Workflow Transformation Programme',
    desc: 'A structured transformation engagement supporting workflow stabilisation, redesign, automation planning, implementation support, and capability development.',
  },
];

export const AdvisorySection = () => (
  <section id="advisory" className="py-20 lg:py-28 bg-slate-50" data-testid="advisory-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-12">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">
          Beyond the Diagnostic
        </div>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-4">
          Advisory and Transformation Support
        </h2>
        <p className="text-slate-600 leading-relaxed text-base">
          Where further support is required, Aurea Digital Solutions can assist through tailored engagements.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {ADVISORY.map(item => (
          <div
            key={item.title}
            data-testid={`advisory-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            className="bg-white border border-slate-200 rounded-xl p-7 hover:border-teal-500 hover:shadow-md transition-all flex flex-col"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-11 h-11 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center flex-shrink-0">
                <item.icon size={22} weight="duotone" className="text-teal-700" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900 leading-tight">{item.title}</div>
                <div className="text-[11px] tracking-[0.16em] font-semibold text-teal-700 uppercase mt-1">
                  Available Upon Consultation
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-5 flex-1">{item.desc}</p>
            <div className="flex gap-2">
              <Button asChild size="sm" className="bg-slate-900 text-white hover:bg-slate-800 font-medium">
                <a href={GENERIC_CONSULTATION_WHATSAPP} target="_blank" rel="noopener noreferrer">
                  <WhatsappLogo size={15} weight="fill" className="mr-1.5" />
                  Request Consultation
                </a>
              </Button>
              <Button asChild size="sm" variant="outline" className="border-slate-300 text-slate-700">
                <a href={GENERIC_CONSULTATION_EMAIL}>
                  <Envelope size={15} weight="bold" className="mr-1.5" />
                  Email
                </a>
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- Why Organisations Choose AWSAI ----------

const REASONS = [
  {
    title: 'Structured Diagnostic Approach',
    desc: 'AWSAI helps organisations understand workflow stability before committing to automation or AI investment.',
  },
  {
    title: 'Management-Oriented Findings',
    desc: 'The output is written for decision makers and translates scores into operational implications and management priorities.',
  },
  {
    title: 'Benchmark-Based Interpretation',
    desc: 'The Professional Report shows where the organisation stands and what gaps should be prioritised.',
  },
  {
    title: 'Practical Transformation Roadmap',
    desc: 'Recommendations are organised into clear actions, priorities, and next steps.',
  },
  {
    title: 'Applicable Across Sectors',
    desc: 'AWSAI can be applied to SMEs, universities, government agencies, healthcare organisations, professional service firms, NGOs, and internal support functions.',
  },
];

export const WhyChooseSection = () => (
  <section id="why-choose" className="py-20 lg:py-28 bg-white" data-testid="why-choose-section">
    <div className="max-w-6xl mx-auto px-6 lg:px-12">
      <div className="max-w-3xl mb-12">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">
          Trusted Approach
        </div>
        <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-4">
          Why Organisations Choose AWSAI
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {REASONS.map((r, idx) => (
          <div key={r.title} className="bg-gradient-to-b from-white to-slate-50 border border-slate-200 rounded-xl p-6">
            <div className="text-3xl font-black text-teal-600/30 mb-2 leading-none">{String(idx + 1).padStart(2, '0')}</div>
            <div className="text-lg font-bold text-slate-900 mb-2">{r.title}</div>
            <p className="text-sm text-slate-600 leading-relaxed">{r.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
